import Information from 'components/Information';
import Link from 'components/Link';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import {FC, useMemo} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import {toCurrency} from 'utils/number';

import MailpointImage from './MailpointImage';
import MailpointSelection from './MailpointSelection';
import PriceCalculation from './PriceCalculation';
import {MailpointsForm, MailpointsInitialData} from './types';

const DisplayMailPointPriceChangeFlaggedBanner = () => {
    const {checkFlag} = useBackend<MailpointsInitialData>();

    if (!checkFlag('mp_banner_2024')) return null;

    const pricingExplanationLink = (
        <Link
            href="https://www.makeleaps.jp/updates/2024/08/mailpoint_price_change-2/"
            isOpenInNewTab={true}
        >
            {t`the news page`}
        </Link>
    );

    return (
        <Information
            className="mx-auto my-3 flex max-w-4xl flex-row p-4"
            icon="info"
            size="sm"
            type="system"
        >
            <span className="font-semibold">{t`Upcoming Price Change: `}</span>
            {jt`Unit price of MailPoints will be changed on October 1st, 2024. See ${pricingExplanationLink} (Japanese site) for more details.`}
        </Information>
    );
};

const Mailpoints: FC = () => {
    const {data} = useBackend<MailpointsInitialData>();

    const form = useForm<MailpointsForm>({
        defaultValues: {
            mailpoints: undefined,
            mode: 'presets',
        },
    });

    const [mailpoints, mode] = form.watch(['mailpoints', 'mode']);

    const multiple =
        mode === 'manual' && data.onlyMultiplesOfTenAllowed ? 10 : 1;

    const value = Number(mailpoints || 0) * multiple;
    const min = multiple === 10 ? data.mpPackMinimum : data.mpMinimum;
    const max = multiple === 10 ? data.mpPackMaximum : data.mpMaximum;

    const isValid = useMemo(() => {
        const mailpointsNumber = Number(mailpoints);

        return mailpointsNumber >= min && mailpointsNumber <= max;
    }, [mailpoints, max, min]);

    const price = toCurrency({
        currency: 'JPY',
        value: data.mpUnitPrice,
    });

    return (
        <FormProvider {...form}>
            <NavigationTabs.Billing selection="buyMailPoints" />
            <DisplayMailPointPriceChangeFlaggedBanner />
            <div className="mx-auto mt-2 flex max-w-4xl flex-row gap-x-12 bg-green-10 p-4">
                <MailpointImage />
                <div className="min-w-[400px] flex-1">
                    <div className="mb-4 flex flex-col gap-y-2 rounded border border-grey-300 bg-grey-50 p-4">
                        <div>
                            {t`You currently have ${data.currentMp} MailPoints.`}
                        </div>
                        <div>{t`Buy additional MailPoints`}</div>
                        <MailpointSelection />
                        <div className="text-sm text-grey-700">
                            {t`${price} (+Tax) per MailPoint`}
                        </div>
                    </div>
                    <div className="grid">
                        <PriceCalculation
                            isValid={isValid}
                            mailpoints={value}
                            unitPrice={data.mpUnitPrice}
                        />
                        <LinkButton
                            aria-label="submit"
                            className="w-full rounded-t-none text-center"
                            disabled={!isValid}
                            href={window.Urls.checkout_mailpoints_view(
                                `${value}`,
                            )}
                            kind="primary"
                            size="lg"
                        >
                            {isValid ? (
                                t`Proceed to Checkout`
                            ) : (
                                <span className="text-sm">
                                    {t`Enter the amount of MailPoints to purchase (${data.mpMinimum}-${data.mpMaximum})`}
                                </span>
                            )}
                        </LinkButton>
                    </div>
                </div>
            </div>
        </FormProvider>
    );
};

export default Mailpoints;
